import { useAuth } from "@babackapp/auth-client/hooks";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnReasons } from "@babackapp/return-widget-components/hooks/use-return-reasons";
import { useComputeReturnFees } from "@babackapp/return-widget-components/queries/use-compute-return-fees";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import {
  resolveReturnModes,
  resolveStoreReturnState,
} from "../utils/return-modes";
import {
  filterStoresByCountry,
  sortStoresByProximity,
} from "../utils/store-selection";
import { useWidgetParams } from "./use-widget-params";

const NO_RETURN_TO_STORE_TAG = "BB_NO_RETURN_TO_STORE";

export function useAvailableReturnModes() {
  const { shopId, orderId } = useWidgetParams();
  const { data: shop } = usePublicShop(shopId);
  const { data: order } = usePublicOrder(orderId);
  const draft = useDraftReturn(orderId);
  const reasons = useReturnReasons(shopId);
  const {
    data: returnFees,
    isPlaceholderData,
    isFetching,
  } = useComputeReturnFees({ orderId, shopId });
  const { user } = useAuth();
  const isMerchant = !!user;

  const draftLineItems = draft?.lineItems ?? [];
  const draftLineItemIds = new Set(draftLineItems.map((li) => li.lineItemId));
  const hasNoReturnToStoreTag = (order?.lineItems ?? []).some(
    (li) =>
      draftLineItemIds.has(li.line_item_id) &&
      li.product?.tags?.includes(NO_RETURN_TO_STORE_TAG),
  );
  const hasStoreReturns =
    shop.returnToStore?.activated === true && !hasNoReturnToStoreTag;
  // An `OVERRIDE_STORE_RETURN` refusal keeps the store on screen, greyed out
  // with its reason, instead of hiding it. The order load only sees rules
  // keyed on the order (tags, dates…); compute-return-fees evaluates the
  // basket like the creation does (BBS-418). A placeholder answer belongs to
  // a previous basket and is ignored.
  const storeReturnState = resolveStoreReturnState({
    orderLoadAnswer: order?.opt?.isStoreReturnAvailable,
    basketAnswer: isPlaceholderData
      ? undefined
      : returnFees?.isStoreReturnAvailable,
    isBasketAnswerPending: isFetching,
  });

  const retractationReasonIds = new Set(
    reasons.filter((r) => r.isRetractation).map((r) => r.id),
  );
  const hasRetractationItem = draftLineItems.some(
    (li) => li.reasonId != null && retractationReasonIds.has(li.reasonId),
  );

  const selectableStores = sortStoresByProximity(
    filterStoresByCountry(
      (shop.returnToStore?.stores ?? []).filter((s) => s.selectableByUser),
      order?.shippingAddress?.country_code,
      isMerchant,
    ),
    order?.shippingAddress?.zip,
  );

  return {
    availableReturnModes: resolveReturnModes({
      // An order loaded before the field existed keeps the carrier mode.
      hasCarrier: order?.opt?.isCarrierReturnAvailable !== false,
      hasStoreReturns,
      hasRetractation:
        shop.preferences?.retractationEnabled === true && hasRetractationItem,
      hasExchangeItem: draftLineItems.some((li) => li.resolution === "exchange"),
      isMerchant,
    }),
    selectableStores,
    hasStoreReturns,
    storeReturnState,
  };
}
